import { useState, useCallback, useRef, useEffect } from 'react';
import { VariableSizeList } from 'react-window';
import useResizeObserver from 'use-resize-observer';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import type { PDFPageProxy } from 'pdfjs-dist';
import type { ListOnItemsRenderedProps } from 'react-window';

import Box from '@mui/material/Box';

import { EMPTY_ARRAY } from '@e-socna/empty';

import { Page } from './Page';
import { PdfPage } from './PdfPage';
import type { ScrollingStatus } from './types/scrolling-status.type';

interface Props {
  readonly gap?: number;
  readonly getPage: (index: number) => Promise<PDFPageProxy> | undefined;
  readonly height?: string | number;
  readonly pageListRef: RefObject<VariableSizeList<unknown> | null>;
  readonly scale?: number;
  readonly setScrollingStatus: Dispatch<SetStateAction<ScrollingStatus>>;
  readonly totalPageCount: number;
  readonly width?: string | number;
}

// MHO : from https://codesandbox.io/s/pdf-viewer-w7wpu
export const Viewer = ({
  gap = 40,
  getPage,
  height = '100%',
  pageListRef,
  scale = 1,
  setScrollingStatus,
  totalPageCount,
  width = '100%',
}: Readonly<Props>) => {
  const [pages, setPages] = useState<readonly PDFPageProxy[]>(EMPTY_ARRAY);

  const listRef = useRef<VariableSizeList | null>(null);

  const {
    ref,
    width: internalWidth = 400,
    height: internalHeight = 600,
  } = useResizeObserver();

  const fetchPage = useCallback(
    (index: number) => {
      if (!pages[index]) {
        const pdfPage = getPage(index);

        if (pdfPage) {
          pdfPage
            .then((page) => {
              setPages((prev) => {
                const next = [...prev];
                next[index] = page;
                return next;
              });
              if (listRef.current) {
                listRef.current.resetAfterIndex(index);
              }
            })
            .catch((error: unknown) => {
              throw error;
            });
        }
      }
    },
    [getPage, pages],
  );

  const handleItemSize = useCallback(
    (index: number) => {
      if (pages[index]) {
        const page = pages[index];
        const viewport = page.getViewport({ scale });
        return viewport.height + gap;
      }
      return 50;
    },
    [pages, scale, gap],
  );

  const handleListRef = useCallback(
    (elem: Readonly<VariableSizeList<unknown>>) => {
      listRef.current = elem;
      pageListRef.current = elem;
    },
    [pageListRef],
  );

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [scale]);

  const handleItemsRendered = useCallback(
    ({ visibleStartIndex }: Readonly<ListOnItemsRenderedProps>) => {
      setScrollingStatus((prev) =>
        prev.isControlled
          ? prev
          : { ...prev, pageNumber: visibleStartIndex + 1 },
      );
    },
    [setScrollingStatus],
  );

  return (
    <Box
      ref={ref}
      sx={{
        width,
        height,
        border: (theme) => `1px solid ${theme.palette.grey[300]}`,
        background: (theme) => theme.palette.grey[100],
        borderRadius: 1,
        maxWidth: '100%',
      }}
    >
      <VariableSizeList
        height={internalHeight}
        itemCount={totalPageCount}
        itemSize={handleItemSize}
        onItemsRendered={handleItemsRendered}
        ref={handleListRef}
        width={internalWidth}
      >
        {({ index, style }) => {
          fetchPage(index);

          return (
            <Page sx={style}>
              <PdfPage index={index} page={pages[index]} scale={scale} />
            </Page>
          );
        }}
      </VariableSizeList>
    </Box>
  );
};
